'use client'

import { useEffect, useState } from 'react'

export function useHashActive() {
  const [hash, setHash] = useState<string>('')

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Get all sections that have IDs on the homepage
    const sections = ['projects', 'partners', 'about', 'contact']

    const update = () => setHash(window.location.hash || '')

    // Function to get the currently visible section
    const getCurrentSection = (): string => {
      const scrollPosition = window.scrollY + 100 // Offset for header height

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId)
        if (element) {
          const rect = element.getBoundingClientRect()
          const elementTop = rect.top + window.scrollY
          const elementBottom = elementTop + rect.height

          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            return `#${sectionId}`
          }
        }
      }

      // If we're at the top or no section is active, return empty
      return window.scrollY < 200 ? '' : hash
    }

    // Function to update hash based on scroll position
    const handleScroll = () => {
      // Only update hash on homepage
      if (window.location.pathname === '/' || window.location.pathname.match(/^\/[a-z]{2}$/)) {
        const currentSection = getCurrentSection()
        const currentHash = window.location.hash

        if (currentSection !== currentHash) {
          // Update URL without triggering navigation
          const newUrl = currentSection
            ? `${window.location.pathname}${currentSection}`
            : window.location.pathname
          window.history.replaceState(null, '', newUrl)
          setHash(currentSection)
        }
      }
    }

    // Initialize on mount
    update()

    // Update on native hash changes and history navigation
    window.addEventListener('hashchange', update)
    window.addEventListener('popstate', update)

    // Add scroll listener for automatic section detection
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('hashchange', update)
      window.removeEventListener('popstate', update)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [hash])

  return { hash, setHash }
}
